// Shared pure upload evidence rules. The API resolves policy windows; clients supply observations.
// This module has no environment, database, timer or destructive-write authority.
export const UPLOAD_VERDICTS = ["arriving", "quiet", "complete", "short", "failed", "lost", "cancelled"];

export const UPLOAD_SESSION_FACT_VALUES = Object.freeze({
	transferState: ["queued", "uploading", "sealed", "cancelled"],
	terminalIntent: ["open", "clean-finish", "recovered-finish", "discarded"],
	processingState: ["not-required", "pending", "processing", "ready", "failed"],
	recoverabilityState: ["not-applicable", "recoverable", "unrecoverable"],
	outputQuality: ["none", "partial", "complete", "failed"]
});

/** Compatibility status is a projection; it must never replace the independent facts. */
export function deriveUploadSessionStatus(facts) {
	if (facts.terminalIntent === "discarded" || facts.transferState === "cancelled") return "cancelled";
	if (facts.processingState === "processing") return "processing";
	if (facts.outputQuality === "complete") return "ready";
	if (facts.outputQuality === "failed" || facts.processingState === "failed") return "failed";
	if (facts.processingState === "ready") return "ready";
	if (facts.recoverabilityState === "unrecoverable") return "abandoned";
	if (facts.transferState === "sealed" || facts.terminalIntent === "clean-finish" || facts.terminalIntent === "recovered-finish")
		return "sealed";
	return facts.transferState === "uploading" ? "uploading" : "requested";
}

/** Read-only view of observations. Missing domain evidence has no verdict, even with a local queue. */
export function projectUploadObservation(observation) {
	const facts = observation.registry?.facts;
	const known = facts && Object.entries(UPLOAD_SESSION_FACT_VALUES).every(([name, values]) => values.includes(facts[name]));
	const status = known ? deriveUploadSessionStatus(facts) : null;
	return {
		takenAt: observation.takenAt,
		verdict:
			hasDomainEvidence(observation.evidence) &&
			Number.isFinite(timestampMs(observation.takenAt)) &&
			["gate", "admin", "customer"].includes(observation.policy?.windows) &&
			["merge", "merge+receipts"].includes(observation.policy?.shortEvidence) &&
			observation.windows
				? classifyUpload(observation.evidence, observation.policy, observation.takenAt, observation.windows)
				: null,
		transferVerified: observation.registry?.transferVerified ?? null,
		transferState: facts?.transferState ?? null,
		terminalIntent: facts?.terminalIntent ?? null,
		processingState: facts?.processingState ?? null,
		recoverabilityState: facts?.recoverabilityState ?? null,
		outputQuality: facts?.outputQuality ?? null,
		discardReason: facts?.discardReason ?? null,
		processingWork: observation.registry?.processingWork ?? null,
		status,
		outputReady: status === "ready" && facts.outputQuality === "complete"
	};
}

function hasDomainEvidence(evidence) {
	return (
		evidence &&
		["recording", "file", "snapshot"].includes(evidence.kind) &&
		["web", "mobile"].includes(evidence.origin) &&
		Number.isFinite(timestampMs(evidence.createdAt)) &&
		["deletedAt", "lastChunkAt", "multipartHeartbeatAt", "awaitingUploadAt"].every(
			(name) => evidence[name] == null || Number.isFinite(timestampMs(evidence[name]))
		) &&
		[
			"hasMinimumData",
			"transcoded",
			"transcodeFailed",
			"resumableFailure",
			"mergePartial",
			"multiRecording",
			"isFileUpload",
			"playableChild"
		].every((name) => typeof evidence[name] === "boolean")
	);
}
function timestampMs(value) {
	return typeof value === "number" ? value : typeof value?.getTime === "function" ? value.getTime() : Date.parse(value);
}
function hasRecoveryWindowElapsed(createdAt, now, windowMs) {
	const createdAtMs = timestampMs(createdAt);
	return !Number.isFinite(createdAtMs) || timestampMs(now) - createdAtMs >= windowMs;
}
export function hasKnownChunkBounds(expectedUploadSize, receivedMaxChunkIndex) {
	return expectedUploadSize != null && expectedUploadSize > 0 && receivedMaxChunkIndex != null;
}
/** Origin filter for web-only surfaces (banner, reminders). Never part of the verdict. */
export function isWebOriginItem(evidence) {
	return evidence.origin === "web";
}
/** @internal shared with uploadStateSummary.ts. */
export function minutesBetween(earlier, later) {
	return (timestampMs(later) - timestampMs(earlier)) / 60_000;
}
// @internal, shared with uploadStateSummary.ts. The latest activity stamp. A recording that has not sent a chunk (and has no
// heartbeat) is judged on createdAt; a stub always includes createdAt. `attemptActivityAt` (UploadSessions.lastActivityAt on the
// current active attempt, the WO-1 rule-2 exception) is folded in only AFTER the domain answer is computed and only through max(),
// so it can delay a verdict but never advance one: an attempt stamp older than the domain answer changes nothing.
export function activityAt(evidence) {
	const stamps = [evidence.lastChunkAt, evidence.multipartHeartbeatAt].filter((stamp) => stamp != null);
	if (evidence.kind !== "recording" || stamps.length === 0) stamps.push(evidence.createdAt);
	const domainActivityMs = Math.max(...stamps.map((stamp) => timestampMs(stamp)));
	const attemptActivityMs = attemptActivityStampMs(evidence);
	return new Date(attemptActivityMs != null && attemptActivityMs > domainActivityMs ? attemptActivityMs : domainActivityMs);
}
// The current-attempt activity stamp, for every kind and transport; null (no opinion) when absent or unusable.
function attemptActivityStampMs(evidence) {
	if (evidence.attemptActivityAt == null) return null;
	const stampMs = timestampMs(evidence.attemptActivityAt);
	return Number.isFinite(stampMs) ? stampMs : null;
}
// Rule 11a's clock for a session that never sent a chunk: createdAt, or the current attempt's activity when that is newer.
function neverChunkedActivityAt(evidence) {
	const attemptActivityMs = attemptActivityStampMs(evidence);
	return attemptActivityMs != null && attemptActivityMs > timestampMs(evidence.createdAt)
		? new Date(attemptActivityMs)
		: evidence.createdAt;
}
function stalenessMinutes(evidence, windows) {
	if (evidence.kind !== "recording" || evidence.isFileUpload) return windows.pendingUploadStalenessMinutes;
	return evidence.origin === "mobile" ? windows.pendingMobileRecordingStalenessMinutes : windows.pendingRecordingStalenessMinutes;
}
/**
 * The staleness window the classifier applies to this item (rules 4, 8, 13 and the C1 hold): 120 min for stubs and video-file
 * uploads, the mobile window for a mobile-origin recording, the web recording window otherwise. Exported so a log that names
 * "the window" names the one the verdict actually used (cold review M5), never a hard-coded web window.
 */
export function uploadItemStalenessMinutes(evidence, windows) {
	return stalenessMinutes(evidence, windows);
}
// Rule 9's suppression window: the admin quiet window under the admin policy, the customer-facing suppression window otherwise.
function transcodeConfirmedCompleteSuppressionMinutes(policy, windows) {
	return policy.windows === "admin"
		? windows.adminTranscodeConfirmedCompleteSuppressionMinutes
		: windows.transcodeConfirmedCompleteSuppressionMinutes;
}
function arrivingWindowMinutes(policy, windows) {
	return policy.windows === "customer" ? windows.transcodeConfirmedCompleteSuppressionMinutes : windows.abandonedQuietMinutes;
}
// A file upload, or a multipart stub: liveness is proven by URL refreshes and persisted part progress (domain facts). A
// chunk-stream recording is never on this lane. The lane (and its 8 h window) applies only when a heartbeat EXISTS for the item:
// `multipartHeartbeatAt` (a multipart stub's MPU makes its createdAt that proxy) or a current-attempt activity stamp. A reserved
// video pick that never started is not on the lane: it stays on the 120-min stub window judged on createdAt.
function isOnHeartbeatLane(evidence) {
	return isOnHeartbeatTransport(evidence) && hasHeartbeatEvidence(evidence);
}
// The transport half of the lane: a video-file upload or a multipart stub, heartbeat or not. A `quiet` verdict on it is never
// "abandoned" (wave 4 fix round 1): with a heartbeat it is the 8 h liveness window, without one it is the 120-min stub window on
// createdAt under EVERY policy (rule 13), never the admin's 5-min arriving window. Only `lost` raises the banner, and the summary's
// abandoned fold depends on this.
export function isOnHeartbeatTransport(evidence) {
	return evidence.isFileUpload || (evidence.kind !== "recording" && evidence.transport === "multipart");
}
function hasHeartbeatEvidence(evidence) {
	return evidence.multipartHeartbeatAt != null || evidence.attemptActivityAt != null;
}
/**
 * A known short tail: both chunk-index bounds known and `expected > received`. A multi-recording session's bounds are telemetry
 * only (the expected/received chunk indexes are unreliable across recorder restarts), so its tail is never evidence. Exported so
 * the readiness gate names the loss shape from the same test the verdict used.
 */
export function hasKnownShortTail(evidence) {
	if (evidence.multiRecording) return false;
	const { expectedChunks, receivedChunks } = evidence;
	return hasKnownChunkBounds(expectedChunks, receivedChunks) && expectedChunks > receivedChunks;
}
// @internal, shared with uploadStateSummary.ts. Short evidence is a known short tail (never for a multi-recording session, see
// hasKnownShortTail), the merge planner's persisted gap (`mergePartial`), or an unreplaced failed child from the current take.
// The latter two are durable domain verdicts and do not depend on the eventually-consistent receipt projection.
export function hasShortEvidence(evidence, policy) {
	if (evidence.confirmedPartReceiptShort === true) return true;
	if (evidence.actionableChildFailure === true) return true;
	if (!hasKnownShortTail(evidence) && !evidence.mergePartial) return false;
	return policy.shortEvidence === "merge" || evidence.receiptShort === true;
}
/**
 * A successfully transcoded, playable recording whose client never supplied a trustworthy
 * completion boundary. This is usable content, but it is not proof that every chunk arrived.
 * Keep this distinct from both confirmed-complete content and actionable missing media.
 */
export function isSettledPlayableUnknown(evidence, now, windows) {
	const outsideRecoveryWindow = minutesBetween(activityAt(evidence), now) > stalenessMinutes(evidence, windows);
	return (
		evidence.kind === "recording" &&
		evidence.deletedAt == null &&
		evidence.sessionType !== "clip_merge" &&
		evidence.finishReceived !== true &&
		evidence.transcoded &&
		!evidence.transcodeFailed &&
		evidence.playableChild &&
		!evidence.mergePartial &&
		evidence.actionableChildFailure !== true &&
		evidence.receiptCoverageComplete !== false &&
		evidence.receiptCoverageComplete !== true &&
		outsideRecoveryWindow &&
		!hasKnownShortTail(evidence) &&
		!hasShortEvidence(evidence, UPLOAD_VERDICT_POLICIES.customer)
	);
}
/** The three policies every surface reads. */
export const UPLOAD_VERDICT_POLICIES = Object.freeze({
	gate: Object.freeze({ windows: "gate", shortEvidence: "merge" }),
	admin: Object.freeze({ windows: "admin", shortEvidence: "merge" }),
	customer: Object.freeze({ windows: "customer", shortEvidence: "merge+receipts" })
});
/**
 * Classify one upload item. First match wins, in the design's rule order: 1, 2, 3, 4, 5, 5b, 6, 6b, 7, 8, 9, 10, 11, 12, 13.
 * Pure: reads only the evidence, the policy, `now` and `windows` (resolved from process.env when omitted).
 */
export function classifyUpload(evidence, policy, now, windows) {
	const isRecording = evidence.kind === "recording";
	const isGate = policy.windows === "gate";
	// 1. The domain row is soft-deleted. A registry `cancelled` on a live row is ignored (the per-recording restore reopens only its
	// own delete's cancel; any other cancel can outlive a restore, and the domain row decides).
	if (evidence.deletedAt != null) return "cancelled";
	if (evidence.verifiedTransferAccepted) {
		return evidence.transcodeFailed || evidence.stubStatus === "failed" || evidence.stubStatus === "abandoned" ? "failed" : "complete";
	}
	const latestActivityAt = activityAt(evidence);
	const ageMinutes = minutesBetween(latestActivityAt, now);
	const staleness = stalenessMinutes(evidence, windows);
	if (isRecording && evidence.transcodeFailed) {
		// 2. Zero-chunk terminalization (develop: S5r with detail `system_gave_up:zero-data-terminal`): the bytes never arrived.
		if (!evidence.hasMinimumData) return "lost";
		// 3. Outside the product recovery window: the same horizon as develop's `mayEndUpload`.
		if (hasRecoveryWindowElapsed(evidence.createdAt, now, windows.uploadRecoveryWindowMs)) return "lost";
		// 4. Still inside the item's window, the client can resume (LSP-20959). Past it, a resumable failure (S5r: the client never
		// delivered the bytes) is loss; a processing failure, or a pre-envelope failure with no terminalReason, is `failed`.
		if (ageMinutes <= staleness) return "quiet";
		return evidence.resumableFailure ? "lost" : "failed";
	}
	if (!isRecording) {
		// 5. Terminal stub statuses: not pending, not loss.
		if (evidence.stubStatus === "failed" || evidence.stubStatus === "abandoned") return "failed";
		// 5b. Zero-byte `requested` phantom aged out (WO-1 reader #1).
		if (evidence.stubStatus === "pending" && evidence.registry?.status === "abandoned") return "lost";
		// 6. The snapshot finalizer writes the stub and the registry `ready` atomically.
		if (evidence.stubStatus === "complete" || (evidence.stubStatus === "pending" && evidence.registry?.status === "ready")) {
			return "complete";
		}
	}
	const shortEvidence = hasShortEvidence(evidence, policy);
	// 6b. A finished, intact session is never pending, whatever its chunk age. The high-water mark is cardinality-blind:
	// [1, 2, 4] and [1, 2, 3, 4] both report received=expected=4, so only the durable finish latch releases this rule.
	if (isRecording && evidence.finishReceived === true && !shortEvidence) return "complete";
	// 7. Bytes may still be coming (including a finished-short session whose tail may still be draining, LSP-21621).
	if (ageMinutes <= arrivingWindowMinutes(policy, windows)) return "arriving";
	if (isRecording) {
		const finished = evidence.finishReceived === true || evidence.transcoded;
		// 8, cold review C1: under the gate, for a session the client never finished, `transcoded` is evidence only after the item's
		// own staleness window. The streaming engine publishes an unfinished
		// session after its upload-inactivity timeout, so a paused mobile capture is transcoded long before its 240-minute window runs
		// out. Admin and customer keep rules 8–10 unchanged.
		if (isGate && evidence.finishReceived !== true && evidence.transcoded && ageMinutes <= staleness) {
			return "quiet";
		}
		// 8. Finished with short evidence: the gate holds inside the staleness window (heldForTruncation); admin/customer banner.
		if (finished && shortEvidence) {
			if (!isGate) return "short";
			return ageMinutes > staleness ? "short" : "quiet";
		}
		// 9. LSP-17965: transcoded but never finished. Silent until the suppression window runs out; equal high-water
		// bounds do not bypass this hold because they cannot prove that an interior chunk arrived.
		if (evidence.transcoded && evidence.finishReceived !== true) {
			// A high-water mark alone is cardinality-blind, but the live receipt projection is not: the
			// single-child natural key makes one row for every index in 1..expected authoritative coverage.
			if (evidence.receiptCoverageComplete === true) return "complete";
			if (ageMinutes > transcodeConfirmedCompleteSuppressionMinutes(policy, windows)) return "short";
			return isGate ? "quiet" : "complete";
		}
		// 10. Finished and nothing proves footage is missing.
		if (finished) return "complete";
		// 11a. The absolute cap applies only to a session that never sent a chunk. It is measured from createdAt, or from the current
		// attempt's activity when that is newer (cold review M3: a never-chunked session whose /uploads attempt is still beating past
		// the cap is not lost). Aliveness only: an older attempt stamp changes nothing.
		if (
			evidence.lastChunkAt == null &&
			evidence.multipartHeartbeatAt == null &&
			minutesBetween(neverChunkedActivityAt(evidence), now) > windows.recordingHoldAbsoluteCapMinutes
		) {
			return "lost";
		}
		// 11b. Develop's reconciler stamped the session awaiting upload (S1) after the client went silent. That verdict is
		// authoritative unless activity is newer than the stamp; it matches develop's banner (masterRecordings.ts).
		if (evidence.awaitingUploadAt != null && latestActivityAt.getTime() <= timestampMs(evidence.awaitingUploadAt)) return "lost";
	}
	// 12/13. The staleness window. A heartbeat-lane item (a video-file upload, a multipart stub) is judged on the heartbeat liveness
	// window (MULTIPART_LIVENESS_WINDOW_MS, 8 h) under EVERY policy, gate included (Andriy review #5): its activity moves only on a
	// URL refresh (about every 4 h), persisted part progress or an /uploads progress beat, so the gate, the admin banner and the loss
	// fold agree on one window.
	if (isOnHeartbeatLane(evidence)) {
		return ageMinutes * 60_000 <= windows.multipartLivenessWindowMs ? "quiet" : "lost";
	}
	return ageMinutes <= staleness ? "quiet" : "lost";
}
